import { useRef, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/apiClient";
import { Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMPTS = [
  "I'm feeling overwhelmed today.",
  "Give me a verse on strength.",
  "How do I lead my family well?",
  "Pray with me for peace.",
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/session").then((r) => {
      setSessionId(r.data.session_id);
      if (r.data.messages?.length) setMessages(r.data.messages);
    }).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
          if (data.done && data.session_id) setSessionId(data.session_id);
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble connecting right now. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone pt-32 pb-32">
      <div className="max-w-[820px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Christ-Centered Companion</span>
          <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight mt-4 flex items-center gap-4">
            Barnabas <Sparkles size={28} className="text-gold" strokeWidth={1.3} />
          </h1>
          <p className="font-sans text-black mt-4">Encouragement, Scripture, and prayer — for whatever you're carrying today.</p>
        </Reveal>

        <div className="mt-12 space-y-8" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="border border-black p-8">
              <p className="font-serif text-2xl text-black italic">"Barnabas — son of encouragement." (Acts 4:36)</p>
              <div className="flex flex-wrap gap-3 mt-8">
                {PROMPTS.map((p, i) => (
                  <button key={p} onClick={() => send(p)} data-testid="prompt-chip"
                    className={`${["bg-[#F5EFE2]", "bg-[#E6ECF7]", "bg-[#E8EEE6]", "bg-[#F4E9EC]"][i % 4]} border border-black px-4 py-2 font-sans text-sm text-black hover:opacity-80 transition-opacity`}>{p}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-right" : ""} data-testid={`msg-${m.role}`}>
              <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">{m.role === "user" ? "You" : "Barnabas"}</span>
              <div className={`mt-2 inline-block text-left max-w-[85%] p-5 ${m.role === "user" ? "bg-charcoal text-bone" : "bg-sand text-charcoal"}`}>
                {m.role === "user" ? (
                  <p className="font-sans leading-relaxed whitespace-pre-line">{m.content}</p>
                ) : m.content ? (
                  <div className="font-sans leading-relaxed space-y-3
                    [&_strong]:font-semibold [&_strong]:text-olive
                    [&_em]:italic [&_em]:text-charcoal/80
                    [&_h1]:font-serif [&_h1]:text-2xl [&_h2]:font-serif [&_h2]:text-xl [&_h3]:font-serif [&_h3]:text-lg
                    [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-1
                    [&_blockquote]:border-l-2 [&_blockquote]:border-gold [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:font-serif [&_blockquote]:text-lg [&_blockquote]:text-charcoal/90
                    [&_a]:text-olive [&_a]:underline [&_hr]:border-charcoal/15 [&_hr]:my-4">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="font-sans leading-relaxed">{busy && i === messages.length - 1 ? "…" : ""}</p>
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-bone/80 backdrop-blur-xl border-t border-charcoal/10 py-4">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="max-w-[820px] mx-auto px-6 md:px-10 flex gap-3" data-testid="chat-form">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Share what's on your heart..." data-testid="chat-input"
            className="flex-1 bg-transparent border-b border-charcoal/30 py-3 font-sans focus:outline-none focus:border-olive transition-colors" />
          <button type="submit" disabled={busy} data-testid="chat-send"
            className="bg-charcoal text-bone px-6 py-3 hover:bg-olive transition-colors disabled:opacity-50 flex items-center gap-2">
            <Send size={16} strokeWidth={1.5} />
          </button>
        </form>
      </div>
    </div>
  );
}
